import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Columns, Download, FileSpreadsheet, Filter, Search, X } from "lucide-react";
import { exportToPdf, exportToExcel } from "#/lib/exportUtils";
import {
  useMembers,
  useMembershipCategories,
  useReportCategoryPayments,
  useReportMemberPayments,
  useReportYearlySummary,
} from "../hooks";
import {
  currentFYDates,
  fyStartYear,
  toFinancialYear,
} from "../utils/financialYear";
import { MEMBERSHIP_PERMISSIONS } from "../permissions";
import { membershipApi } from "../api";
import type {
  MemberCompany,
  MemberPaymentRow,
  MemberStatus,
  MembershipWorkflowStage,
  PaymentStatus,
  ReportMemberStatus,
  YearlySummaryResponse,
} from "../types";
import { MembershipAccess } from "../components/MembershipAccess";
import { MembershipPageHeader } from "../components/MembershipPageHeader";
import { MembershipPagination } from "../components/MembershipPagination";
import { MembershipStatusBadge } from "../components/MembershipStatusBadge";

// ── Constants ──────────────────────────────────────────────────────────────────
const PAGE_SIZE = 20;

const MONTHS = [
  "", "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

export function monthLabel(month: number | null | undefined): string {
  if (month == null) return "";
  return MONTHS[month] ?? "";
}

// ── Column definitions (Member Register tab) ───────────────────────────────────
interface ColDef {
  key: string;
  label: string;
  mandatory?: boolean;
  render: (m: MemberCompany, idx: number, pageOffset: number) => React.ReactNode;
  csv: (m: MemberCompany) => string;
}

function fmtDate(v?: string | null): string {
  if (!v) return "";
  try {
    return new Date(v).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
  } catch { return v; }
}
function fmt(v?: string | null) { return v ?? ""; }
function bool(v: boolean) { return v ? "Yes" : "No"; }
function fmtKes(v: number | null | undefined): string {
  if (v == null) return "—";
  return v.toLocaleString("en-KE", { minimumFractionDigits: 2 });
}

const ALL_COLS: ColDef[] = [
  {
    key: "#", label: "#", mandatory: true,
    render: (_, idx, off) => <span className="text-xs text-slate-400">{off + idx + 1}</span>,
    csv: () => "",
  },
  {
    key: "companyName", label: "Company", mandatory: true,
    render: (m) => <span className="font-black text-slate-900 whitespace-nowrap">{m.companyName}</span>,
    csv: (m) => m.companyName,
  },
  {
    key: "categoryName", label: "Category", mandatory: true,
    render: (m) => <>{m.categoryName}</>,
    csv: (m) => m.categoryName,
  },
  {
    key: "membershipCode", label: "Code",
    render: (m) => <span className="font-mono text-xs">{fmt(m.membershipCode)}</span>,
    csv: (m) => fmt(m.membershipCode),
  },
  {
    key: "status", label: "Status",
    render: (m) => <MembershipStatusBadge value={m.status} />,
    csv: (m) => m.status.replace(/_/g, " "),
  },
  {
    key: "workflowStage", label: "Workflow Stage",
    render: (m) => <span className="whitespace-nowrap text-xs text-slate-600">{m.workflowStage?.replace(/_/g, " ") ?? "—"}</span>,
    csv: (m) => m.workflowStage?.replace(/_/g, " ") ?? "",
  },
  {
    key: "source", label: "Source",
    render: (m) => <span className="text-xs text-slate-600">{m.source.replace(/_/g, " ")}</span>,
    csv: (m) => m.source.replace(/_/g, " "),
  },
  {
    key: "boardApprovalDate", label: "Board Date",
    render: (m) => <span className="whitespace-nowrap text-xs text-slate-600">{fmtDate(m.boardApprovalDate)}</span>,
    csv: (m) => fmtDate(m.boardApprovalDate),
  },
  {
    key: "boardDecisionBy", label: "Board Decision By",
    render: (m) => <span className="text-xs text-slate-600">{fmt(m.boardDecisionBy)}</span>,
    csv: (m) => fmt(m.boardDecisionBy),
  },
  {
    key: "boardNotes", label: "Board Notes",
    render: (m) => <span className="text-xs text-slate-600">{fmt(m.boardNotes)}</span>,
    csv: (m) => fmt(m.boardNotes),
  },
  {
    key: "paymentConfirmedAt", label: "Payment Confirmed",
    render: (m) => <span className="whitespace-nowrap text-xs text-slate-600">{fmtDate(m.paymentConfirmedAt)}</span>,
    csv: (m) => fmtDate(m.paymentConfirmedAt),
  },
  {
    key: "paymentConfirmedBy", label: "Payment Confirmed By",
    render: (m) => <span className="text-xs text-slate-600">{fmt(m.paymentConfirmedBy)}</span>,
    csv: (m) => fmt(m.paymentConfirmedBy),
  },
  {
    key: "ictCompletedAt", label: "ICT Completed",
    render: (m) => <span className="whitespace-nowrap text-xs text-slate-600">{fmtDate(m.ictCompletedAt)}</span>,
    csv: (m) => fmtDate(m.ictCompletedAt),
  },
  {
    key: "ictCompletedBy", label: "ICT Completed By",
    render: (m) => <span className="text-xs text-slate-600">{fmt(m.ictCompletedBy)}</span>,
    csv: (m) => fmt(m.ictCompletedBy),
  },
  {
    key: "trainingCompletedAt", label: "Training Completed",
    render: (m) => <span className="whitespace-nowrap text-xs text-slate-600">{fmtDate(m.trainingCompletedAt)}</span>,
    csv: (m) => fmtDate(m.trainingCompletedAt),
  },
  {
    key: "trainingCompletedBy", label: "Training Completed By",
    render: (m) => <span className="text-xs text-slate-600">{fmt(m.trainingCompletedBy)}</span>,
    csv: (m) => fmt(m.trainingCompletedBy),
  },
  {
    key: "activeDate", label: "Active Date",
    render: (m) => <span className="whitespace-nowrap text-xs text-slate-600">{fmtDate(m.activeDate)}</span>,
    csv: (m) => fmtDate(m.activeDate),
  },
  {
    key: "certificateIssued", label: "Certificate",
    render: (m) => m.certificateIssued
      ? <span className="rounded-xl bg-emerald-100 px-2 py-0.5 text-xs font-bold text-emerald-700">Issued</span>
      : <span className="text-xs text-slate-400">No</span>,
    csv: (m) => bool(m.certificateIssued),
  },
  {
    key: "certificateIssueDate", label: "Certificate Date",
    render: (m) => <span className="whitespace-nowrap text-xs text-slate-600">{fmtDate(m.certificateIssueDate)}</span>,
    csv: (m) => fmtDate(m.certificateIssueDate),
  },
  {
    key: "auctionAccessAllowed", label: "Auction Access",
    render: (m) => m.auctionAccessAllowed
      ? <span className="rounded-xl bg-blue-100 px-2 py-0.5 text-xs font-bold text-blue-700">Yes</span>
      : <span className="text-xs text-slate-400">No</span>,
    csv: (m) => bool(m.auctionAccessAllowed),
  },
  {
    key: "active", label: "Active",
    render: (m) => <span className={`text-xs font-bold ${m.active ? "text-emerald-700" : "text-red-600"}`}>{m.active ? "Yes" : "No"}</span>,
    csv: (m) => bool(m.active),
  },
  {
    key: "notes", label: "Notes",
    render: (m) => <span className="text-xs text-slate-500 max-w-[160px] truncate block">{m.notes ?? ""}</span>,
    csv: (m) => fmt(m.notes),
  },
];

const DEFAULT_ON = new Set([
  "membershipCode", "status", "workflowStage", "source",
  "boardApprovalDate", "activeDate", "certificateIssued", "auctionAccessAllowed",
]);

// ── Filter options ─────────────────────────────────────────────────────────────
const STATUS_OPTIONS: { label: string; value: MemberStatus | "" }[] = [
  { label: "All Statuses", value: "" },
  { label: "Prospect", value: "PROSPECT" },
  { label: "Pending Review", value: "PENDING_REVIEW" },
  { label: "Board Pending", value: "BOARD_PENDING" },
  { label: "Board Approved", value: "BOARD_APPROVED" },
  { label: "Board Rejected", value: "BOARD_REJECTED" },
  { label: "Payment Pending", value: "PAYMENT_PENDING" },
  { label: "Payment Confirmed", value: "PAYMENT_CONFIRMED" },
  { label: "ICT Pending", value: "ICT_PENDING" },
  { label: "ICT Completed", value: "ICT_COMPLETED" },
  { label: "Training Pending", value: "TRAINING_PENDING" },
  { label: "Training Completed", value: "TRAINING_COMPLETED" },
  { label: "Active Member", value: "ACTIVE_MEMBER" },
  { label: "Suspended", value: "SUSPENDED" },
  { label: "Inactive", value: "INACTIVE" },
];

const STAGE_OPTIONS: { label: string; value: MembershipWorkflowStage | "" }[] = [
  { label: "All Stages", value: "" },
  { label: "Prospect Created", value: "PROSPECT_CREATED" },
  { label: "Application Received", value: "APPLICATION_RECEIVED" },
  { label: "Under Membership Review", value: "UNDER_MEMBERSHIP_REVIEW" },
  { label: "Submitted to Board", value: "SUBMITTED_TO_BOARD" },
  { label: "Board Approved", value: "BOARD_APPROVED" },
  { label: "Board Rejected", value: "BOARD_REJECTED" },
  { label: "Payment Pending", value: "PAYMENT_PENDING" },
  { label: "Payment Confirmed", value: "PAYMENT_CONFIRMED" },
  { label: "ICT Account Pending", value: "ICT_ACCOUNT_PENDING" },
  { label: "ICT Account Created", value: "ICT_ACCOUNT_CREATED" },
  { label: "Training Pending", value: "TRAINING_PENDING" },
  { label: "Training Completed", value: "TRAINING_COMPLETED" },
  { label: "Fully Onboarded", value: "FULLY_ONBOARDED" },
  { label: "Suspended", value: "SUSPENDED" },
  { label: "Inactive", value: "INACTIVE" },
];

const REPORT_STATUS_OPTIONS: { label: string; value: ReportMemberStatus | "" }[] = [
  { label: "All Statuses", value: "" },
  { label: "Pending", value: "PENDING" },
  { label: "Under Review", value: "UNDER_REVIEW" },
  { label: "Approved", value: "APPROVED" },
  { label: "Active Member", value: "ACTIVE_MEMBER" },
  { label: "Suspended", value: "SUSPENDED" },
  { label: "Terminated", value: "TERMINATED" },
];

// ── Client-side PDF export (members) — routes through the shared, letterheaded exporter ───────
function exportMembersPdf(members: MemberCompany[], activeCols: ColDef[], filters: Record<string, string>) {
  const exportCols = activeCols.filter((c) => c.key !== "#");
  const fp = Object.entries(filters).filter(([, v]) => v).map(([k, v]) => `${k}: ${v}`);
  const subtitle = [fp.length ? `Filters — ${fp.join("  ·  ")}` : null, `${members.length} records`]
    .filter(Boolean)
    .join("  ·  ");

  return exportToPdf({
    title: "Membership Report",
    subtitle,
    headers: exportCols.map((c) => c.label),
    rows: members.map((m) => exportCols.map((c) => c.csv(m))),
    filename: `membership-report-${new Date().toISOString().slice(0, 10)}`,
  });
}

// ── Client-side Excel export (members) — quick, always-available alternative to the ──────────
// backend /reports/members/excel endpoint (which silently no-ops if not yet implemented — see
// downloadServerMembersExcel below). Was a plain CSV; converted 2026-07-13 to the shared,
// letterheaded xlsx exporter so it carries the same branding as every other export in the app.
function exportMembersExcel(members: MemberCompany[], activeCols: ColDef[]) {
  const exportCols = activeCols.filter((c) => c.key !== "#");
  return exportToExcel(
    "Membership Report",
    exportCols.map((c) => c.label),
    members.map((m) => exportCols.map((c) => c.csv(m))),
    `membership-report-${new Date().toISOString().slice(0, 10)}`,
  );
}

// ── Payment status badge ───────────────────────────────────────────────────────
function PaymentStatusBadge({ status }: { status: PaymentStatus | null }) {
  if (status === null) {
    return <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs font-bold text-slate-500">Not Invoiced</span>;
  }
  const map: Record<PaymentStatus, string> = {
    PAID: "bg-emerald-100 text-emerald-700",
    NOT_PAID: "bg-amber-100 text-amber-700",
    PARTIALLY_PAID: "bg-blue-100 text-blue-700",
    OVERDUE: "bg-red-100 text-red-700",
    WAIVED: "bg-slate-100 text-slate-600",
    REFUNDED: "bg-purple-100 text-purple-700",
  };
  return (
    <span className={`rounded-full px-2 py-0.5 text-xs font-black ${map[status] ?? "bg-slate-100 text-slate-500"}`}>
      {status.replace(/_/g, " ")}
    </span>
  );
}

function MemberStatusBadge({ status }: { status: ReportMemberStatus }) {
  const map: Record<ReportMemberStatus, string> = {
    PENDING: "bg-amber-100 text-amber-700",
    UNDER_REVIEW: "bg-blue-100 text-blue-700",
    APPROVED: "bg-emerald-100 text-emerald-700",
    ACTIVE_MEMBER: "bg-emerald-700 text-white",
    SUSPENDED: "bg-red-100 text-red-700",
    TERMINATED: "bg-slate-200 text-slate-600",
  };
  return (
    <span className={`rounded-full px-2 py-0.5 text-xs font-black whitespace-nowrap ${map[status] ?? "bg-slate-100 text-slate-500"}`}>
      {status.replace(/_/g, " ")}
    </span>
  );
}

type ReportTab = "members" | "payments" | "yearly";

// ── Page ───────────────────────────────────────────────────────────────────────
export default function MembershipReportsPage() {
  const [reportTab, setReportTab] = useState<ReportTab>("members");

  // ── Members tab state ────────────────────────────────────────────────────────
  const [page, setPage] = useState(0);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState<MemberStatus | "">("");
  const [workflowStage, setWorkflowStage] = useState<MembershipWorkflowStage | "">("");
  const [categoryId, setCategoryId] = useState<number | "">("");
  const [membershipYear, setMembershipYear] = useState<number | "">("");
  const [activeDateFrom, setActiveDateFrom] = useState("");
  const [activeDateTo, setActiveDateTo] = useState("");
  const [downloading, setDownloading] = useState<"pdf" | "quick-excel" | "server-excel" | "server-pdf" | null>(null);
  const [showColPicker, setShowColPicker] = useState(false);
  const [enabledCols, setEnabledCols] = useState<Set<string>>(() => new Set([
    ...ALL_COLS.filter((c) => c.mandatory).map((c) => c.key),
    ...DEFAULT_ON,
  ]));

  // ── Payment tab state ────────────────────────────────────────────────────────
  const { from: defaultPayFrom, to: defaultPayTo } = currentFYDates();
  const [payFromDate, setPayFromDate] = useState(defaultPayFrom);
  const [payToDate, setPayToDate] = useState(defaultPayTo);
  const payFY = payFromDate ? toFinancialYear(new Date(payFromDate)) : "";
  const [payMemberStatus, setPayMemberStatus] = useState<ReportMemberStatus | "">("");
  // client-side filters
  const [paySearch, setPaySearch] = useState("");
  const [payCategoryFilter, setPayCategoryFilter] = useState("");
  const [payStatusFilter, setPayStatusFilter] = useState<PaymentStatus | "NOT_INVOICED" | "">("");
  const [payPage, setPayPage] = useState(0);
  const [payDownloading, setPayDownloading] = useState<"excel" | "pdf" | null>(null);

  const categoriesQuery = useMembershipCategories();

  // Members tab queries
  const params = useMemo(
    () => ({ page, size: PAGE_SIZE, search, status, workflowStage, categoryId, membershipYear }),
    [page, search, status, workflowStage, categoryId, membershipYear]
  );
  const membersQuery = useMembers(params);

  const allParams = useMemo(
    () => ({ page: 0, size: 100, search, status, workflowStage, categoryId, membershipYear }),
    [search, status, workflowStage, categoryId, membershipYear]
  );
  const allMembersQuery = useMembers(allParams);

  // Payment tab queries
  const paymentQuery = useReportMemberPayments({ financialYear: payFY, status: payMemberStatus });
  const categoryPaymentQuery = useReportCategoryPayments(payFY);

  // Yearly summary
  const yearlySummaryQuery = useReportYearlySummary();

  const resetPage = () => setPage(0);

  const activeCols = ALL_COLS.filter((c) => enabledCols.has(c.key));

  const toggleCol = (key: string) => {
    setEnabledCols((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  // ── Client-side filtered payment rows ────────────────────────────────────────
  const allPayRows = paymentQuery.data ?? [];
  const filteredPayRows = useMemo(() => {
    return allPayRows.filter((r) => {
      if (paySearch && !r.companyName.toLowerCase().includes(paySearch.toLowerCase())) return false;
      if (payCategoryFilter && r.categoryName !== payCategoryFilter) return false;
      if (payStatusFilter === "NOT_INVOICED") {
        if (r.paymentStatus !== null) return false;
      } else if (payStatusFilter) {
        if (r.paymentStatus !== payStatusFilter) return false;
      }
      return true;
    });
  }, [allPayRows, paySearch, payCategoryFilter, payStatusFilter]);

  const payTotalPages = Math.max(1, Math.ceil(filteredPayRows.length / PAGE_SIZE));
  const pagedPayRows = filteredPayRows.slice(payPage * PAGE_SIZE, (payPage + 1) * PAGE_SIZE);

  // ── Export handlers ──────────────────────────────────────────────────────────
  const getExportData = async () => {
    const result = await allMembersQuery.refetch();
    return result.data?.content ?? [];
  };

  const categoryLabel = categoriesQuery.data?.find((c) => c.id === categoryId)?.name ?? "";
  const exportFilters = {
    Status: status.replace(/_/g, " "),
    Stage: workflowStage.replace(/_/g, " "),
    Category: categoryLabel,
    Search: search,
  };

  const downloadClientPdf = async () => {
    setDownloading("pdf");
    try {
      const members = await getExportData();
      await exportMembersPdf(members, activeCols, exportFilters);
    } finally { setDownloading(null); }
  };

  const downloadClientExcel = async () => {
    setDownloading("quick-excel");
    try {
      const members = await getExportData();
      await exportMembersExcel(members, activeCols);
    } finally { setDownloading(null); }
  };

  const downloadServerMembersExcel = async () => {
    setDownloading("server-excel");
    try {
      await membershipApi.downloadMembersExcel(
        membershipYear !== "" ? membershipYear : undefined,
      );
    } catch { /* server export not yet available — fall back silently */ }
    finally { setDownloading(null); }
  };

  const downloadServerMembersPdf = async () => {
    setDownloading("server-pdf");
    try {
      await membershipApi.downloadMembersPdf(
        membershipYear !== "" ? membershipYear : undefined,
      );
    } catch { await downloadClientPdf(); }
    finally { setDownloading(null); }
  };

  const downloadPaymentsExcel = async () => {
    setPayDownloading("excel");
    try {
      await membershipApi.downloadPaymentsExcel(payFY);
    } finally { setPayDownloading(null); }
  };

  const downloadPaymentsPdf = async () => {
    setPayDownloading("pdf");
    try {
      await membershipApi.downloadPaymentsPdf(payFY);
    } finally { setPayDownloading(null); }
  };

  const data = membersQuery.data;
  const rows = data?.content ?? [];
  const hasFilters = !!(search || status || workflowStage || categoryId !== "" || membershipYear !== "" || activeDateFrom || activeDateTo);
  const hasPayFilters = !!(paySearch || payCategoryFilter || payStatusFilter);

  // Unique categories from payment rows for dropdown
  const payCategories = useMemo(
    () => [...new Set(allPayRows.map((r) => r.categoryName))].sort(),
    [allPayRows]
  );

  return (
    <div className="min-h-screen bg-slate-50 p-6">
      <MembershipPageHeader
        title="Membership Reports"
        actions={
          <MembershipAccess permissions={[MEMBERSHIP_PERMISSIONS.REPORT_VIEW]}>
            <div className="flex items-center gap-2">
              {reportTab === "members" && (
                <>
                  <button type="button" onClick={() => setShowColPicker((v) => !v)}
                    className={`inline-flex items-center gap-2 rounded-2xl border px-4 py-3 text-sm font-black transition ${showColPicker ? "border-emerald-700 bg-emerald-700 text-white" : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50"}`}>
                    <Columns className="h-4 w-4" />
                    Columns ({activeCols.filter(c => c.key !== "#").length})
                  </button>
                  <button type="button" onClick={downloadClientExcel} disabled={!!downloading}
                    title="Builds instantly in your browser — use if the server Excel export below isn't available"
                    className="inline-flex items-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm font-black text-slate-700 hover:bg-slate-50 disabled:opacity-60">
                    <FileSpreadsheet className="h-4 w-4 text-green-600" />
                    {downloading === "quick-excel" ? "Preparing…" : "Excel (Quick)"}
                  </button>
                  <button type="button" onClick={downloadServerMembersExcel} disabled={!!downloading}
                    className="inline-flex items-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm font-black text-slate-700 hover:bg-slate-50 disabled:opacity-60">
                    <FileSpreadsheet className="h-4 w-4 text-emerald-600" />
                    {downloading === "server-excel" ? "Preparing…" : "Excel"}
                  </button>
                  <button type="button" onClick={downloadServerMembersPdf} disabled={!!downloading}
                    className="inline-flex items-center gap-2 rounded-2xl bg-emerald-700 px-4 py-3 text-sm font-black text-white hover:bg-emerald-800 disabled:opacity-60">
                    <Download className="h-4 w-4" />
                    {downloading === "server-pdf" ? "Preparing…" : "PDF"}
                  </button>
                </>
              )}
              {reportTab === "payments" && (
                <MembershipAccess permissions={[MEMBERSHIP_PERMISSIONS.FINANCE_VIEW, MEMBERSHIP_PERMISSIONS.FINANCE_UPDATE]}>
                  <>
                    <button type="button" onClick={downloadPaymentsExcel} disabled={!!payDownloading}
                      className="inline-flex items-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm font-black text-slate-700 hover:bg-slate-50 disabled:opacity-60">
                      <FileSpreadsheet className="h-4 w-4 text-emerald-600" />
                      {payDownloading === "excel" ? "Preparing…" : "Export Excel"}
                    </button>
                    <button type="button" onClick={downloadPaymentsPdf} disabled={!!payDownloading}
                      className="inline-flex items-center gap-2 rounded-2xl bg-emerald-700 px-4 py-3 text-sm font-black text-white hover:bg-emerald-800 disabled:opacity-60">
                      <Download className="h-4 w-4" />
                      {payDownloading === "pdf" ? "Preparing…" : "Export PDF"}
                    </button>
                  </>
                </MembershipAccess>
              )}
            </div>
          </MembershipAccess>
        }
      />

      {/* ── Tab switcher ──────────────────────────────────────────────────── */}
      <div className="mt-6 flex gap-2">
        {(["members", "payments", "yearly"] as ReportTab[]).map((t) => (
          <button
            key={t}
            type="button"
            onClick={() => setReportTab(t)}
            className={`rounded-2xl px-5 py-3 text-sm font-black transition ${
              reportTab === t
                ? "bg-emerald-700 text-white shadow-lg shadow-emerald-700/20"
                : "bg-white border border-slate-200 text-slate-600 hover:bg-slate-50"
            }`}
          >
            {t === "members" ? "Member Register" : t === "payments" ? "Payment Analysis" : "Yearly Summary"}
          </button>
        ))}
      </div>

      {/* ════════════════════════════════════════════════════════════════
          MEMBERS TAB
      ════════════════════════════════════════════════════════════════ */}
      {reportTab === "members" && (
        <>
          {showColPicker && (
            <div className="mt-4 rounded-3xl border border-emerald-200 bg-white p-5 shadow-sm">
              <div className="flex items-center justify-between mb-4">
                <p className="text-sm font-black text-slate-700">Select columns to display and export</p>
                <button type="button" onClick={() => setShowColPicker(false)} className="text-slate-400 hover:text-slate-600">
                  <X className="h-4 w-4" />
                </button>
              </div>
              <div className="grid gap-2 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-4">
                {ALL_COLS.filter((c) => c.key !== "#").map((col) => (
                  <label key={col.key}
                    className={`flex cursor-pointer items-center gap-2 rounded-2xl border px-3 py-2.5 transition ${col.mandatory ? "border-emerald-200 bg-emerald-50 cursor-not-allowed" : "border-slate-200 hover:border-slate-300"}`}>
                    <input type="checkbox" checked={enabledCols.has(col.key)} disabled={col.mandatory}
                      onChange={() => toggleCol(col.key)} className="accent-emerald-700" />
                    <span className="text-sm font-bold text-slate-700">{col.label}</span>
                    {col.mandatory && <span className="ml-auto text-xs text-emerald-600 font-black">Required</span>}
                  </label>
                ))}
              </div>
            </div>
          )}

          <div className="mt-4 rounded-3xl border border-slate-200 bg-white p-4 shadow-sm">
            <div className="flex items-center gap-2 mb-3">
              <Filter className="h-4 w-4 text-slate-400" />
              <span className="text-sm font-black text-slate-700">Filters</span>
              {hasFilters && (
                <button type="button"
                  onClick={() => { setSearch(""); setStatus(""); setWorkflowStage(""); setCategoryId(""); setMembershipYear(""); setActiveDateFrom(""); setActiveDateTo(""); resetPage(); }}
                  className="ml-auto inline-flex items-center gap-1 rounded-xl border border-red-200 bg-red-50 px-2.5 py-1 text-xs font-bold text-red-600 hover:bg-red-100">
                  <X className="h-3 w-3" /> Clear all
                </button>
              )}
            </div>
            <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-5">
              <div className="relative">
                <Search className="absolute left-4 top-3.5 h-4 w-4 text-slate-400" />
                <input value={search} onChange={(e) => { setSearch(e.target.value); resetPage(); }}
                  placeholder="Search name, code…"
                  className="w-full rounded-2xl border border-slate-200 py-3 pl-11 pr-4 text-sm outline-none focus:border-emerald-500 focus:ring-4 focus:ring-emerald-100" />
              </div>
              <select value={categoryId} onChange={(e) => { setCategoryId(e.target.value === "" ? "" : Number(e.target.value)); resetPage(); }} className="rounded-2xl border border-slate-200 px-4 py-3 text-sm">
                <option value="">All Categories</option>
                {(categoriesQuery.data ?? []).map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
              <select value={status} onChange={(e) => { setStatus(e.target.value as MemberStatus | ""); resetPage(); }} className="rounded-2xl border border-slate-200 px-4 py-3 text-sm">
                {STATUS_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
              <select value={workflowStage} onChange={(e) => { setWorkflowStage(e.target.value as MembershipWorkflowStage | ""); resetPage(); }} className="rounded-2xl border border-slate-200 px-4 py-3 text-sm">
                {STAGE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
              <div className="relative">
                <label className="absolute -top-5 left-1 text-[10px] font-black uppercase tracking-wide text-slate-400">Membership year from</label>
                <input
                  type="date"
                  value={membershipYear !== "" ? `${membershipYear}-04-01` : ""}
                  onChange={(e) => {
                    if (!e.target.value) { setMembershipYear(""); resetPage(); return; }
                    setMembershipYear(fyStartYear(toFinancialYear(new Date(e.target.value))));
                    resetPage();
                  }}
                  className="w-full rounded-2xl border border-slate-200 px-4 py-3 text-sm outline-none focus:border-emerald-500 focus:ring-4 focus:ring-emerald-100"
                />
              </div>
            </div>
            <div className="mt-3 grid gap-3 md:grid-cols-2 xl:grid-cols-4">
              <div className="xl:col-span-2 flex items-center gap-2">
                <span className="text-xs font-black uppercase tracking-wide text-slate-400 whitespace-nowrap">Active date</span>
                <input type="date" value={activeDateFrom} onChange={(e) => { setActiveDateFrom(e.target.value); resetPage(); }}
                  className="flex-1 rounded-2xl border border-slate-200 px-4 py-2.5 text-sm outline-none focus:border-emerald-500 focus:ring-4 focus:ring-emerald-100" />
                <span className="text-xs text-slate-400">to</span>
                <input type="date" value={activeDateTo} onChange={(e) => { setActiveDateTo(e.target.value); resetPage(); }}
                  min={activeDateFrom}
                  className="flex-1 rounded-2xl border border-slate-200 px-4 py-2.5 text-sm outline-none focus:border-emerald-500 focus:ring-4 focus:ring-emerald-100" />
              </div>
              {(activeDateFrom || activeDateTo) && (
                <button type="button" onClick={() => { setActiveDateFrom(""); setActiveDateTo(""); resetPage(); }}
                  className="inline-flex items-center gap-1 rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-xs font-bold text-red-600 hover:bg-red-100 w-fit">
                  <X className="h-3 w-3" /> Clear dates
                </button>
              )}
            </div>
          </div>

          <div className="mt-4 rounded-3xl border border-slate-200 bg-white shadow-sm">
            <div className="flex items-center justify-between border-b border-slate-100 px-5 py-4">
              <p className="text-sm font-bold text-slate-500">
                {membersQuery.isLoading ? "Loading…" : `${data?.totalElements ?? 0} members`}
              </p>
              <p className="text-xs text-slate-400">Page {(data?.number ?? 0) + 1} of {data?.totalPages ?? 1}</p>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-left text-xs font-black uppercase tracking-wide text-slate-500">
                  <tr>
                    {activeCols.map((c) => <th key={c.key} className="px-4 py-3 whitespace-nowrap">{c.label}</th>)}
                    <th className="px-4 py-3" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {membersQuery.isLoading ? (
                    <tr><td colSpan={activeCols.length + 1} className="px-4 py-8 text-center text-slate-400">Loading members…</td></tr>
                  ) : rows.length === 0 ? (
                    <tr><td colSpan={activeCols.length + 1} className="px-4 py-8 text-center text-slate-400">No members match the current filters.</td></tr>
                  ) : (
                    rows.map((m, idx) => (
                      <tr key={m.id} className="hover:bg-slate-50 transition-colors">
                        {activeCols.map((c) => <td key={c.key} className="px-4 py-3">{c.render(m, idx, page * PAGE_SIZE)}</td>)}
                        <td className="px-4 py-3">
                          <Link to={`/hr/membership/members/${m.id}`} className="rounded-xl bg-emerald-700 px-3 py-1.5 text-xs font-black text-white hover:bg-emerald-800 whitespace-nowrap">
                            View
                          </Link>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
            {data && data.totalPages > 1 && (
              <div className="border-t border-slate-100 px-5 py-4">
                <MembershipPagination page={data} onPageChange={setPage} />
              </div>
            )}
          </div>
        </>
      )}

      {/* ════════════════════════════════════════════════════════════════
          PAYMENT ANALYSIS TAB
      ════════════════════════════════════════════════════════════════ */}
      {reportTab === "payments" && (
        <MembershipAccess
          permissions={[MEMBERSHIP_PERMISSIONS.FINANCE_VIEW, MEMBERSHIP_PERMISSIONS.FINANCE_UPDATE]}
          fallback={
            <div className="mt-6 rounded-3xl border border-slate-200 bg-white p-12 text-center shadow-sm">
              <p className="text-sm font-bold text-slate-500">Finance permission required to view payment data.</p>
            </div>
          }
        >
          <>
            {/* ── Category summary cards ──────────────────────────────────── */}
            {(categoryPaymentQuery.data ?? []).length > 0 && (
              <div className="mt-4 grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
                {categoryPaymentQuery.data!.map((c) => {
                  const rate = c.totalBilled > 0 ? (c.totalPaid / c.totalBilled) * 100 : 0;
                  return (
                    <div key={c.categoryId} className="rounded-3xl border border-slate-200 bg-white p-4 shadow-sm">
                      <p className="text-xs font-black uppercase tracking-wide text-slate-400 truncate">{c.categoryName}</p>
                      <p className="mt-1 text-2xl font-black text-slate-900">{c.memberCount}</p>
                      <p className="text-xs text-slate-500">members · {c.feeRecordCount} invoiced</p>
                      <div className="mt-3 flex items-center gap-2">
                        <div className="h-1.5 flex-1 rounded-full bg-slate-100 overflow-hidden">
                          <div
                            className={`h-full rounded-full transition-all ${rate >= 80 ? "bg-emerald-500" : rate >= 50 ? "bg-amber-400" : "bg-red-400"}`}
                            style={{ width: `${Math.min(rate, 100)}%` }}
                          />
                        </div>
                        <span className="text-xs font-black text-slate-600">{rate.toFixed(0)}%</span>
                      </div>
                      <div className="mt-2 grid grid-cols-3 gap-1 text-center">
                        <div>
                          <p className="text-[10px] text-slate-400">Paid</p>
                          <p className="text-xs font-black text-emerald-700">{c.paidCount}</p>
                        </div>
                        <div>
                          <p className="text-[10px] text-slate-400">Unpaid</p>
                          <p className="text-xs font-black text-amber-700">{c.unpaidCount}</p>
                        </div>
                        <div>
                          <p className="text-[10px] text-slate-400">Overdue</p>
                          <p className="text-xs font-black text-red-600">{c.overdueCount}</p>
                        </div>
                      </div>
                      <div className="mt-2 border-t border-slate-100 pt-2">
                        <div className="flex justify-between text-xs">
                          <span className="text-slate-400">Outstanding</span>
                          <span className="font-black text-red-600">KES {fmtKes(c.totalOutstanding)}</span>
                        </div>
                        <div className="flex justify-between text-xs mt-0.5">
                          <span className="text-slate-400">Billed</span>
                          <span className="font-bold text-slate-700">KES {fmtKes(c.totalBilled)}</span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* ── Filters ──────────────────────────────────────────────────── */}
            <div className="mt-4 rounded-3xl border border-slate-200 bg-white p-4 shadow-sm">
              <div className="flex items-center gap-2 mb-3">
                <Filter className="h-4 w-4 text-slate-400" />
                <span className="text-sm font-black text-slate-700">Filters</span>
                {hasPayFilters && (
                  <button type="button"
                    onClick={() => { setPaySearch(""); setPayCategoryFilter(""); setPayStatusFilter(""); setPayPage(0); }}
                    className="ml-auto inline-flex items-center gap-1 rounded-xl border border-red-200 bg-red-50 px-2.5 py-1 text-xs font-bold text-red-600 hover:bg-red-100">
                    <X className="h-3 w-3" /> Clear filters
                  </button>
                )}
              </div>

              {/* Date range inputs */}
              <div className="flex flex-wrap items-end gap-4 mb-3">
                <div>
                  <label className="block text-xs font-black uppercase tracking-wide text-slate-400 mb-1">From</label>
                  <input
                    type="date"
                    value={payFromDate}
                    onChange={(e) => { setPayFromDate(e.target.value); setPayPage(0); }}
                    className="rounded-2xl border border-slate-200 px-4 py-2.5 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs font-black uppercase tracking-wide text-slate-400 mb-1">To</label>
                  <input
                    type="date"
                    value={payToDate}
                    onChange={(e) => { setPayToDate(e.target.value); setPayPage(0); }}
                    className="rounded-2xl border border-slate-200 px-4 py-2.5 text-sm"
                  />
                </div>
                {payFY && (
                  <span className="mb-0.5 rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1.5 text-xs font-black text-emerald-700">
                    {payFY}
                  </span>
                )}
              </div>

              <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
                {/* Search */}
                <div className="relative">
                  <Search className="absolute left-4 top-3.5 h-4 w-4 text-slate-400" />
                  <input value={paySearch} onChange={(e) => { setPaySearch(e.target.value); setPayPage(0); }}
                    placeholder="Search company…"
                    className="w-full rounded-2xl border border-slate-200 py-3 pl-11 pr-4 text-sm outline-none focus:border-emerald-500 focus:ring-4 focus:ring-emerald-100" />
                </div>
                {/* Member status (server-side) */}
                <select value={payMemberStatus} onChange={(e) => { setPayMemberStatus(e.target.value as ReportMemberStatus | ""); setPayPage(0); }} className="rounded-2xl border border-slate-200 px-4 py-3 text-sm">
                  {REPORT_STATUS_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
                {/* Category (client-side) */}
                <select value={payCategoryFilter} onChange={(e) => { setPayCategoryFilter(e.target.value); setPayPage(0); }} className="rounded-2xl border border-slate-200 px-4 py-3 text-sm">
                  <option value="">All Categories</option>
                  {payCategories.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
                {/* Payment status (client-side) */}
                <select value={payStatusFilter} onChange={(e) => { setPayStatusFilter(e.target.value as PaymentStatus | "NOT_INVOICED" | ""); setPayPage(0); }} className="rounded-2xl border border-slate-200 px-4 py-3 text-sm">
                  <option value="">All Payment Statuses</option>
                  <option value="PAID">Paid</option>
                  <option value="NOT_PAID">Not Paid</option>
                  <option value="PARTIALLY_PAID">Partially Paid</option>
                  <option value="OVERDUE">Overdue</option>
                  <option value="WAIVED">Waived</option>
                  <option value="REFUNDED">Refunded</option>
                  <option value="NOT_INVOICED">Not Invoiced</option>
                </select>
              </div>
            </div>

            {/* ── Payment table ─────────────────────────────────────────────── */}
            <div className="mt-4 rounded-3xl border border-slate-200 bg-white shadow-sm">
              <div className="flex items-center justify-between border-b border-slate-100 px-5 py-4">
                <div>
                  <p className="text-sm font-bold text-slate-500">
                    {paymentQuery.isLoading
                      ? "Loading…"
                      : `${filteredPayRows.length} records${allPayRows.length !== filteredPayRows.length ? ` (${allPayRows.length} total)` : ""}`}
                    {payFY && <span className="ml-2 rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-black text-emerald-700">{payFY}</span>}
                  </p>
                  {paymentQuery.error && (
                    <p className="text-xs text-amber-600 mt-0.5">Endpoint not yet available: <code className="bg-slate-100 px-1 rounded">GET /reports/member-payments</code></p>
                  )}
                </div>
                <p className="text-xs text-slate-400">Page {payPage + 1} of {payTotalPages}</p>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 text-left text-xs font-black uppercase tracking-wide text-slate-500">
                    <tr>
                      <th className="px-4 py-3">#</th>
                      <th className="px-4 py-3 whitespace-nowrap">Company</th>
                      <th className="px-4 py-3">Code</th>
                      <th className="px-4 py-3">Category</th>
                      <th className="px-4 py-3 whitespace-nowrap">Joined</th>
                      <th className="px-4 py-3 whitespace-nowrap">Board Date</th>
                      <th className="px-4 py-3 whitespace-nowrap">Member Status</th>
                      <th className="px-4 py-3 whitespace-nowrap">FY</th>
                      <th className="px-4 py-3 whitespace-nowrap">Payment Status</th>
                      <th className="px-4 py-3 text-right whitespace-nowrap">Billed (KES)</th>
                      <th className="px-4 py-3 text-right whitespace-nowrap">Paid (KES)</th>
                      <th className="px-4 py-3 text-right whitespace-nowrap">Outstanding</th>
                      <th className="px-4 py-3 whitespace-nowrap">Due Date</th>
                      <th className="px-4 py-3 whitespace-nowrap">Paid Date</th>
                      <th className="px-4 py-3 whitespace-nowrap">Reference</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {paymentQuery.isLoading ? (
                      <tr><td colSpan={15} className="px-4 py-8 text-center text-slate-400">Loading payment records…</td></tr>
                    ) : paymentQuery.error ? (
                      <tr>
                        <td colSpan={15} className="px-4 py-8 text-center">
                          <p className="text-sm text-slate-500 font-bold">No data available</p>
                        </td>
                      </tr>
                    ) : pagedPayRows.length === 0 ? (
                      <tr><td colSpan={15} className="px-4 py-8 text-center text-slate-400">No records match the current filters.</td></tr>
                    ) : (
                      pagedPayRows.map((r: MemberPaymentRow, idx) => (
                        <tr key={`${r.memberId}-${r.financialYear}`} className="hover:bg-slate-50 transition-colors">
                          <td className="px-4 py-3 text-xs text-slate-400">{payPage * PAGE_SIZE + idx + 1}</td>
                          <td className="px-4 py-3 font-black text-slate-900 whitespace-nowrap">{r.companyName}</td>
                          <td className="px-4 py-3 font-mono text-xs text-slate-500">{r.membershipCode ?? "—"}</td>
                          <td className="px-4 py-3 whitespace-nowrap text-slate-600">{r.categoryName}</td>
                          <td className="px-4 py-3 whitespace-nowrap text-xs text-slate-600">
                            {r.membershipYear
                              ? `${r.membershipYear}${r.membershipMonth ? ` · ${monthLabel(r.membershipMonth)}` : ""}`
                              : "—"}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-xs text-slate-600">{fmtDate(r.boardApprovalDate)}</td>
                          <td className="px-4 py-3"><MemberStatusBadge status={r.memberStatus} /></td>
                          <td className="px-4 py-3 whitespace-nowrap">
                            {r.financialYear
                              ? <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs font-bold text-slate-600">{r.financialYear}</span>
                              : <span className="text-xs text-slate-400">—</span>}
                          </td>
                          <td className="px-4 py-3"><PaymentStatusBadge status={r.paymentStatus} /></td>
                          <td className="px-4 py-3 text-right whitespace-nowrap font-bold text-slate-700">{fmtKes(r.amount)}</td>
                          <td className={`px-4 py-3 text-right whitespace-nowrap font-bold ${r.paymentStatus === "PAID" ? "text-emerald-700" : "text-slate-700"}`}>
                            {fmtKes(r.amountPaid)}
                          </td>
                          <td className={`px-4 py-3 text-right whitespace-nowrap font-black ${(r.outstanding ?? 0) > 0 ? "text-red-600" : "text-slate-400"}`}>
                            {r.outstanding != null ? fmtKes(r.outstanding) : "—"}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-xs text-slate-600">{fmtDate(r.dueDate)}</td>
                          <td className="px-4 py-3 whitespace-nowrap text-xs text-slate-600">{fmtDate(r.paymentDate)}</td>
                          <td className="px-4 py-3 text-xs text-slate-500">{r.paymentReference ?? "—"}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              {payTotalPages > 1 && (
                <div className="border-t border-slate-100 px-5 py-4 flex items-center justify-between">
                  <button
                    type="button"
                    onClick={() => setPayPage((p) => Math.max(0, p - 1))}
                    disabled={payPage === 0}
                    className="rounded-xl border border-slate-200 px-3 py-2 text-xs font-bold text-slate-600 hover:bg-slate-50 disabled:opacity-40"
                  >
                    ← Previous
                  </button>
                  <span className="text-xs text-slate-500">Page {payPage + 1} of {payTotalPages}</span>
                  <button
                    type="button"
                    onClick={() => setPayPage((p) => Math.min(payTotalPages - 1, p + 1))}
                    disabled={payPage >= payTotalPages - 1}
                    className="rounded-xl border border-slate-200 px-3 py-2 text-xs font-bold text-slate-600 hover:bg-slate-50 disabled:opacity-40"
                  >
                    Next →
                  </button>
                </div>
              )}
            </div>
          </>
        </MembershipAccess>
      )}

      {/* ════════════════════════════════════════════════════════════════
          YEARLY SUMMARY TAB
      ════════════════════════════════════════════════════════════════ */}
      {reportTab === "yearly" && (
        <MembershipAccess
          permissions={[MEMBERSHIP_PERMISSIONS.FINANCE_VIEW, MEMBERSHIP_PERMISSIONS.REPORT_VIEW]}
          fallback={
            <div className="mt-6 rounded-3xl border border-slate-200 bg-white p-12 text-center shadow-sm">
              <p className="text-sm font-bold text-slate-500">Report permission required.</p>
            </div>
          }
        >
          <div className="mt-4 rounded-3xl border border-slate-200 bg-white shadow-sm">
            <div className="border-b border-slate-100 px-5 py-4">
              <p className="text-sm font-bold text-slate-700">Board-Approval Cohorts &amp; Year Financials</p>
            </div>

            {yearlySummaryQuery.isLoading ? (
              <div className="px-4 py-12 text-center text-sm text-slate-400">Loading yearly summary…</div>
            ) : yearlySummaryQuery.error ? (
              <div className="px-4 py-12 text-center">
                <p className="text-sm font-bold text-slate-500">No data available</p>
              </div>
            ) : (yearlySummaryQuery.data ?? []).length === 0 ? (
              <div className="px-4 py-12 text-center text-sm text-slate-400">No data available.</div>
            ) : (
              <>
                {/* KPI summary row */}
                {(() => {
                  const all = yearlySummaryQuery.data ?? [];
                  const totalBilled = all.reduce((s, y) => s + y.totalBilled, 0);
                  const totalPaid = all.reduce((s, y) => s + y.totalPaid, 0);
                  const totalOutstanding = all.reduce((s, y) => s + y.totalOutstanding, 0);
                  const totalMembers = all.reduce((s, y) => s + y.newMembersApproved, 0);
                  return (
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-slate-100 border-b border-slate-100">
                      {[
                        { label: "Total Members Approved", value: totalMembers.toLocaleString(), color: "text-slate-900" },
                        { label: "Total Billed", value: `KES ${fmtKes(totalBilled)}`, color: "text-slate-900" },
                        { label: "Total Paid", value: `KES ${fmtKes(totalPaid)}`, color: "text-emerald-700" },
                        { label: "Total Outstanding", value: `KES ${fmtKes(totalOutstanding)}`, color: "text-red-600" },
                      ].map((kpi) => (
                        <div key={kpi.label} className="bg-white px-5 py-4">
                          <p className="text-xs text-slate-400 font-black uppercase tracking-wide">{kpi.label}</p>
                          <p className={`mt-1 text-xl font-black ${kpi.color}`}>{kpi.value}</p>
                        </div>
                      ))}
                    </div>
                  );
                })()}

                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50 text-left text-xs font-black uppercase tracking-wide text-slate-500">
                      <tr>
                        <th className="px-4 py-3">Year</th>
                        <th className="px-4 py-3 text-right">Approved</th>
                        <th className="px-4 py-3 text-right whitespace-nowrap">Active by Year End</th>
                        <th className="px-4 py-3 text-right">March</th>
                        <th className="px-4 py-3 text-right">July</th>
                        <th className="px-4 py-3 text-right">November</th>
                        <th className="px-4 py-3 text-right">Other</th>
                        <th className="px-4 py-3 text-right whitespace-nowrap">Billed (KES)</th>
                        <th className="px-4 py-3 text-right whitespace-nowrap">Paid (KES)</th>
                        <th className="px-4 py-3 text-right whitespace-nowrap">Outstanding (KES)</th>
                        <th className="px-4 py-3 text-right">Rate</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {(yearlySummaryQuery.data as YearlySummaryResponse[]).map((y) => {
                        const rate = y.totalBilled > 0 ? (y.totalPaid / y.totalBilled) * 100 : 0;
                        return (
                          <tr key={y.membershipYear} className="hover:bg-slate-50 transition-colors">
                            <td className="px-4 py-3 font-black text-slate-900">{y.membershipYear}</td>
                            <td className="px-4 py-3 text-right font-bold text-slate-700">{y.newMembersApproved}</td>
                            <td className="px-4 py-3 text-right text-emerald-700 font-bold">{y.activeByYearEnd}</td>
                            {/* cohorts */}
                            <td className="px-4 py-3 text-right text-slate-600">
                              {y.marchCohort > 0 ? <span className="rounded-full bg-blue-50 px-2 py-0.5 font-bold text-blue-700">{y.marchCohort}</span> : <span className="text-slate-300">—</span>}
                            </td>
                            <td className="px-4 py-3 text-right text-slate-600">
                              {y.julyCohort > 0 ? <span className="rounded-full bg-amber-50 px-2 py-0.5 font-bold text-amber-700">{y.julyCohort}</span> : <span className="text-slate-300">—</span>}
                            </td>
                            <td className="px-4 py-3 text-right text-slate-600">
                              {y.novemberCohort > 0 ? <span className="rounded-full bg-purple-50 px-2 py-0.5 font-bold text-purple-700">{y.novemberCohort}</span> : <span className="text-slate-300">—</span>}
                            </td>
                            <td className="px-4 py-3 text-right text-slate-600">
                              {y.otherCohort > 0 ? <span className="rounded-full bg-slate-100 px-2 py-0.5 font-bold text-slate-600">{y.otherCohort}</span> : <span className="text-slate-300">—</span>}
                            </td>
                            {/* financials */}
                            <td className="px-4 py-3 text-right whitespace-nowrap text-slate-600">{fmtKes(y.totalBilled)}</td>
                            <td className="px-4 py-3 text-right whitespace-nowrap font-bold text-emerald-700">{fmtKes(y.totalPaid)}</td>
                            <td className={`px-4 py-3 text-right whitespace-nowrap font-black ${y.totalOutstanding > 0 ? "text-red-600" : "text-slate-400"}`}>
                              {y.totalOutstanding > 0 ? fmtKes(y.totalOutstanding) : "—"}
                            </td>
                            <td className="px-4 py-3 text-right">
                              <div className="flex items-center justify-end gap-2">
                                <div className="h-1.5 w-16 rounded-full bg-slate-200 overflow-hidden">
                                  <div
                                    className={`h-full rounded-full ${rate >= 80 ? "bg-emerald-500" : rate >= 50 ? "bg-amber-400" : "bg-red-400"}`}
                                    style={{ width: `${Math.min(rate, 100)}%` }}
                                  />
                                </div>
                                <span className={`text-xs font-black ${rate >= 80 ? "text-emerald-600" : rate >= 50 ? "text-amber-600" : "text-red-600"}`}>
                                  {rate.toFixed(0)}%
                                </span>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </div>
        </MembershipAccess>
      )}
    </div>
  );
}
